/**
 * One cached cloud-order conflict awaiting an admin decision.
 *
 * A conflict is an offline change — a status change or a field edit — whose
 * cloud copy moved before it could be replayed. The app refuses to guess, so
 * this card shows both sides and offers the choice.
 *
 * The label and value helpers below live here rather than in a shared module:
 * every one of them is used only by this card. The manual order queue card uses
 * a different set entirely.
 *
 * Moved out of ReconciliationView. See TCA-26081204.
 */
import React from 'react';
import { AlertTriangle, CheckCircle2, Loader2, WifiOff } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDeliveryDateDisplay } from '@/utils/dateUtils';

const statusLabel = (status) => {
  if (status === 'new') return 'New / Quarantine';
  if (status === 'accepted') return 'Accepted';
  if (status === 'designed') return 'Designed';
  if (status === 'on_route') return 'On route';
  if (status === 'delivered') return 'Delivered';
  if (status === 'failed') return 'Failed';
  return status || 'Unknown';
};

const fieldLabel = (field) => ({
  status: 'Status',
  designed_at: 'Designed time',
  designed_by_id: 'Designer credited',
  designed_marked_by_id: 'Marked designed by',
  assigned_designer_id: 'Assigned designer',
  order_number: 'External order #',
  order_accepted_date: 'Accepted date',
  recipient_name: 'Recipient name',
  recipient_phone: 'Recipient phone',
  recipient_street_address: 'Street address',
  recipient_street_address_2: 'Apt / suite',
  recipient_city: 'City',
  recipient_state: 'State',
  recipient_zip_code: 'ZIP code',
}[field] || field);

const formatReviewTimestamp = (value) => {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not recorded';
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${hh}:${min} on ${yyyy}-${mm}-${dd}`;
};

const valueLabel = (field, value, userNames = {}) => {
  if (field === 'status') return statusLabel(value);
  if (field === 'designed_at') return formatReviewTimestamp(value);
  if (field === 'order_accepted_date') return value || 'Not recorded';
  if (field.endsWith('_id')) {
    if (value === null || value === undefined || value === '') return 'Not recorded';
    return userNames[value] || 'Unknown user';
  }
  if (value === null || value === undefined || value === '') return 'Not recorded';
  return String(value);
};

const hasReviewValue = (field, source = {}) => {
  if (field === 'status') return true;
  const value = source[field];
  return value !== null && value !== undefined && value !== '';
};

const ReviewRows = ({ source, fields, userNames }) => (
  <>
    {fields.filter(field => hasReviewValue(field, source)).map(field => (
      <div key={field}>
        <span className="text-stone-500">{fieldLabel(field)}: </span>
        <span>{valueLabel(field, source[field], userNames)}</span>
      </div>
    ))}
  </>
);

const ConflictCard = ({
  item,
  isAdmin,
  resolvingId,
  onUseOfflineChange,
  onDiscard,
  userNames,
  actionLabels = {},
}) => {
  const mutation = item.mutation || {};
  const order = item.order || {};
  const base = mutation.base_snapshot || {};
  const patch = mutation.local_patch || {};
  const cloud = mutation.cloud_order_snapshot || mutation.cloud_snapshot || {};
  const cloudRefreshedAt = mutation.cloud_order_snapshot_refreshed_at;
  const isTerminalReplayFailure = mutation.state === 'failed_terminal';
  const isFieldEdit = mutation.operation_type === 'cloud_order_field_edit';
  const isAdminEdit = isFieldEdit && mutation.field_group === 'admin';
  const fields = isFieldEdit
    ? (isAdminEdit ? [
      'status',
      'designed_at',
      'designed_by_id',
      'designed_marked_by_id',
      'assigned_designer_id',
      'order_number',
      'order_accepted_date',
    ] : [
      'recipient_name',
      'recipient_phone',
      'recipient_street_address',
      'recipient_street_address_2',
      'recipient_city',
      'recipient_state',
      'recipient_zip_code',
    ])
    : ['status', 'designed_at', 'designed_by_id', 'designed_marked_by_id', 'assigned_designer_id'];
  const conflictTypeLabel = isFieldEdit
    ? (isAdminEdit ? 'admin edit' : 'recipient edit')
    : 'status';
  const held = mutation.state === 'submission_uncertain' || !!mutation.predecessor_id;
  const busy = resolvingId === mutation.id || held;
  const changedBy = (
    (mutation.actor_id && userNames[mutation.actor_id])
    || mutation.actor_username
    || 'Unknown user'
  );
  const changedAt = formatReviewTimestamp(mutation.created_at || mutation.updated_at);

  return (
    <Card
      className="border-amber-300 bg-amber-50"
      data-testid={`cached-${isFieldEdit ? 'field' : 'status'}-conflict-${mutation.id}`}
    >
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <AlertTriangle className="h-4 w-4 text-amber-700" />
              Offline {conflictTypeLabel} {isTerminalReplayFailure ? 'sync problem' : 'conflict'}
              <span className="text-stone-900">
                {order.internal_order_number || mutation.target_id}
              </span>
            </CardTitle>
            <CardDescription>
              {held && <span role="status">An earlier submission has an unconfirmed outcome. Your saved changes are retained; this action will not be sent again automatically. </span>}
              {order.recipient_name || 'Cached order'}
              {order.delivery_date ? ` · ${formatDeliveryDateDisplay(order.delivery_date)}` : ''}
              {mutation.actor_username ? ` · changed offline by ${mutation.actor_username}` : ''}
            </CardDescription>
          </div>
          {isAdmin ? (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={busy}
                onClick={() => onDiscard(item)}
                data-testid={`discard-cached-status-conflict-${mutation.id}`}
              >
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {actionLabels.discard || (
                  isTerminalReplayFailure ? 'Discard offline change' : 'Keep online version'
                )}
              </Button>
              {!isTerminalReplayFailure ? (
                <Button
                  type="button"
                  disabled={busy}
                  onClick={() => onUseOfflineChange(item)}
                  data-testid={`use-cached-status-conflict-${mutation.id}`}
                >
                  {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {actionLabels.apply || 'Apply offline change'}
                </Button>
              ) : null}
            </div>
          ) : (
            <div
              className="rounded border border-amber-200 bg-white px-3 py-2 text-sm text-stone-600"
              data-testid={`cached-status-conflict-readonly-${mutation.id}`}
            >
              Admin required to resolve
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="rounded border border-amber-200 bg-white p-3 text-stone-700">
          {isTerminalReplayFailure
            ? 'This offline change could not be understood by the current cloud replay contract. It was not applied and was not classified as a business-data conflict.'
            : 'This computer changed the order while offline, but the online order changed before the computer synced. Choose which version should win.'}
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="rounded border bg-white p-3">
            <div className="mb-1 font-medium text-stone-700">When this computer went offline</div>
            <ReviewRows source={base} fields={fields} userNames={userNames} />
          </div>
          <div className="rounded border bg-white p-3">
            <div className="mb-1 font-medium text-stone-700">What this computer changed offline</div>
            <ReviewRows source={patch} fields={fields} userNames={userNames} />
            <div className="mt-2 border-t border-stone-100 pt-2">
              <div>
                <span className="text-stone-500">Changed by: </span>
                <span>{changedBy}</span>
              </div>
              <div>
                <span className="text-stone-500">Changed at: </span>
                <span>{changedAt}</span>
              </div>
            </div>
          </div>
          <div className="rounded border bg-white p-3">
            <div className="mb-1 font-medium text-stone-700">Current online version</div>
            {Object.keys(cloud).length > 0 ? (
              <ReviewRows source={cloud} fields={fields} userNames={userNames} />
            ) : (
              <div className="text-stone-500">The online state was not recorded during replay.</div>
            )}
            {cloudRefreshedAt ? (
              <div className="mt-2 border-t border-stone-100 pt-2 text-xs text-stone-500">
                Checked: {formatReviewTimestamp(cloudRefreshedAt)}
              </div>
            ) : null}
          </div>
        </div>
        {mutation.last_replay_detail ? (
          <div className="rounded border border-amber-200 bg-white p-3 text-stone-600">
            Sync note: {isTerminalReplayFailure
              ? 'cloud replay contract rejected the queued change'
              : 'online order changed before sync'}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default ConflictCard;
